import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ReactHighcharts from 'react-highcharts'
import { BarChart3, Check, Gauge, LayoutDashboard, LineChart, PieChart, Table as TableIcon } from 'lucide-react'
import { hcColors, hcFontFamily } from 'modules/chart'
import {
  DashboardWidget,
  formatWidgetValue,
  resolveWidgetDisplay,
  widgetContentSignature,
  widgetToneClass,
} from 'modules/widgetSpec'
import { addWidgetToDashboard, selectAddedWidgetSignatures } from 'features/dashboardWidgets/dashboardWidgetsSlice'
import WidgetInfoTooltip from 'sharedComponents/DashboardWidgets/WidgetInfoTooltip'

interface WidgetPreviewCardProps {
  spec: DashboardWidget
}

const kindIcon = (spec: DashboardWidget) => {
  if (spec.kind === 'metric') return Gauge
  if (spec.kind === 'table') return TableIcon
  if (spec.chart_type === 'pie') return PieChart
  if (spec.chart_type === 'line') return LineChart
  return BarChart3
}

const kindLabel = (spec: DashboardWidget) =>
  spec.kind === 'chart' ? `${spec.chart_type || 'column'} chart` : spec.kind

const formatCell = (value: any): string => {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'number' && isFinite(value)) {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value)
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

/** Highcharts config for a chart-kind preview (column / pie / line per chart_type) */
const buildPreviewChartConfig = (spec: DashboardWidget) => {
  const preview = spec.preview
  if (!preview || preview.kind !== 'chart') return null
  const base = {
    credits: { enabled: false },
    title: { text: null },
    colors: hcColors,
    // animation:false keeps the chart from replaying its intro on every
    // re-render (e.g. while the chat scrolls)
    chart: { backgroundColor: 'transparent', animation: false, height: 220, style: { fontFamily: hcFontFamily } },
    legend: { enabled: preview.series.length > 1 },
  }
  if (spec.chart_type === 'pie') {
    return {
      ...base,
      chart: { ...base.chart, type: 'pie' },
      plotOptions: {
        series: { animation: false },
        pie: { dataLabels: { enabled: true, format: '<b>{point.name}</b>: {point.percentage:.1f}%' } },
      },
      series: [
        {
          name: preview.series[0]?.name || spec.title,
          data: preview.categories.map((category, i) => ({
            name: category,
            y: preview.series[0]?.data?.[i] ?? null,
            color: hcColors[i % hcColors.length],
          })),
        },
      ],
    }
  }
  return {
    ...base,
    chart: { ...base.chart, type: spec.chart_type === 'line' ? 'line' : 'column' },
    xAxis: {
      categories: preview.categories,
      labels: { style: { fontSize: '10px' } },
      tickInterval: Math.max(1, Math.ceil(preview.categories.length / 8)),
    },
    yAxis: { title: { text: null } },
    plotOptions: { series: { animation: false }, column: { borderRadius: 3 } },
    series: preview.series.map((s, i) => ({ name: s.name, data: s.data, color: hcColors[i % hcColors.length] })),
  }
}

const WidgetPreviewCardBase: React.FC<WidgetPreviewCardProps> = ({ spec }) => {
  const dispatch = useDispatch<any>()
  const addedSignatures = useSelector(selectAddedWidgetSignatures)
  const [addStatus, setAddStatus] = useState<'idle' | 'adding' | 'added' | 'failed'>('idle')
  // Guard on content signature (not id): the fenced block is re-parsed with a
  // fresh random id on every render, so an id-based check would never match after
  // a remount and would let the same widget be added twice.
  const signature = useMemo(() => widgetContentSignature(spec), [spec])
  const added = addStatus === 'added' || addedSignatures.includes(signature)

  const chartConfig = useMemo(() => buildPreviewChartConfig(spec), [spec])

  const handleAdd = async () => {
    if (added || addStatus === 'adding') return
    setAddStatus('adding')
    try {
      await dispatch(addWidgetToDashboard(spec)).unwrap()
      setAddStatus('added')
    } catch {
      setAddStatus('failed')
    }
  }

  const KindIcon = kindIcon(spec)
  const preview = spec.preview

  let body: React.ReactNode
  if (!preview) {
    body = (
      <div className="tw-rounded-lg tw-border tw-border-gray-100 tw-bg-gray-50 tw-px-3 tw-py-4 tw-text-sm tw-text-gray-500">
        Preview unavailable — add to dashboard to load data
      </div>
    )
  } else if (preview.kind === 'metric') {
    const resolved = resolveWidgetDisplay(preview, spec.display, spec.format)
    const bigText = resolved?.text ?? preview.text ?? null
    const hasNumber = typeof preview.value === 'number' && isFinite(preview.value)
    const subtitle =
      resolved && preview.text
        ? hasNumber
          ? `${preview.text} · ${formatWidgetValue(preview.value, spec.format)}`
          : preview.text
        : formatWidgetValue(preview.value, spec.format)
    body = (
      <div className="tw-py-2">
        {bigText ? (
          <>
            <div
              className={`tw-text-3xl tw-font-bold ${resolved ? widgetToneClass(resolved.tone) : 'tw-text-gray-900'}`}
            >
              {bigText}
            </div>
            {spec.display?.showValue !== false && !resolved?.templated && (hasNumber || (resolved && preview.text)) ? (
              <div className="tw-mt-1 tw-text-xs tw-text-gray-500">{subtitle}</div>
            ) : null}
          </>
        ) : (
          <div className="tw-text-3xl tw-font-bold tw-text-gray-900">
            {formatWidgetValue(preview.value, spec.format)}
          </div>
        )}
      </div>
    )
  } else if (preview.kind === 'chart' && chartConfig) {
    body = <ReactHighcharts config={chartConfig} isPureConfig={true} />
  } else if (preview.kind === 'table') {
    const columns = preview.columns.length ? preview.columns : Object.keys(preview.rows[0] || {})
    body = (
      <div className="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-gray-100">
        <table className="tw-w-full tw-text-xs">
          <thead className="tw-bg-gray-50">
            <tr>
              {columns.map((col) => (
                <th key={col} className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-600">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.rows.slice(0, 5).map((row, i) => (
              <tr key={i} className="tw-border-t tw-border-gray-100">
                {columns.map((col) => (
                  <td key={col} className="tw-px-3 tw-py-2 tw-text-gray-700 tw-whitespace-nowrap">
                    {formatCell(row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  } else {
    body = (
      <div className="tw-rounded-lg tw-border tw-border-gray-100 tw-bg-gray-50 tw-px-3 tw-py-4 tw-text-sm tw-text-gray-500">
        Preview unavailable — add to dashboard to load data
      </div>
    )
  }

  return (
    <div className="tw-my-4 tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-p-4 tw-shadow-sm">
      <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-min-w-0">
        <span className="tw-font-semibold tw-text-gray-800 tw-truncate">{spec.title}</span>
        <WidgetInfoTooltip widget={spec} />
        <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-indigo-100 tw-bg-indigo-50 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-indigo-600">
          <KindIcon size={11} />
          {kindLabel(spec)}
        </span>
      </div>
      {body}
      <div className="tw-mt-3 tw-flex tw-items-center tw-gap-3">
        {added ? (
          <>
            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-green-600">
              <Check size={15} />
              Added
            </span>
            <a href="/dashboard?view=overview" className="tw-text-sm tw-text-blue-600 hover:tw-underline">
              View dashboard
            </a>
          </>
        ) : (
          <button
            type="button"
            onClick={handleAdd}
            disabled={addStatus === 'adding'}
            className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border-0 tw-bg-blue-600 tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-text-white tw-cursor-pointer tw-shadow-sm hover:tw-bg-blue-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            <LayoutDashboard size={14} />
            {addStatus === 'adding' ? 'Adding…' : 'Add to Dashboard'}
          </button>
        )}
        {addStatus === 'failed' && (
          <span className="tw-text-xs tw-text-red-500">Could not save — try again</span>
        )}
      </div>
    </div>
  )
}

// toWidgetSpec mints a fresh random id on every re-parse when the fenced JSON
// has no id, so the id is excluded from the signature — otherwise the memo
// would never hit and the chart would rebuild on every parent re-render
// (e.g. during scroll), exactly what InlineChartCard's memo prevents.
const widgetSpecSignature = (spec: DashboardWidget): string => JSON.stringify({ ...spec, id: '' })

const WidgetPreviewCard = React.memo(
  WidgetPreviewCardBase,
  (prev, next) => widgetSpecSignature(prev.spec) === widgetSpecSignature(next.spec),
)

export default WidgetPreviewCard
